import { AdditionalFeeModel } from './AdditionalFee.model';
import { PriceModel } from './Price.model';
import { ModelContext } from './ModelContext';
import { LineItemDiscountModel } from './LineItemDiscount.model';
import { SPECS } from '../../../../constants';
import { cartV2 } from '@wix/ecom';

export class SummaryModel {
  public priceSummary: {
    discount?: PriceModel;
    delivery?: PriceModel;
    tax?: PriceModel;
  };
  public additionalFees?: AdditionalFeeModel[];
  public giftCardAmount?: PriceModel;
  public payNow: PriceModel;
  public hasCustomViolations: boolean;
  public calculationErrors?: {
    carrierErrors?: {
      errors?: {
        error?: {
          validationError?: { fieldViolations?: { field?: string }[] };
        };
      }[];
    };
    taxCalculationError?: {
      validationError?: {
        fieldViolations?: { field?: string; description?: string }[];
      };
    };
  };
  public taxExemptPrice?: PriceModel;
  public lineItemDiscounts?: LineItemDiscountModel[];
  public priceVerificationToken?: string;
  public deliveryMethodCodeFromSummary?: string;

  constructor(model: SummaryModel) {
    this.priceSummary = model.priceSummary;
    this.additionalFees = model.additionalFees;
    this.giftCardAmount = model.giftCardAmount;
    this.payNow = model.payNow;
    this.hasCustomViolations = model.hasCustomViolations;
    this.calculationErrors = model.calculationErrors;
    this.taxExemptPrice = model.taxExemptPrice;
    this.lineItemDiscounts = model.lineItemDiscounts;
    this.priceVerificationToken = model.priceVerificationToken;
    this.deliveryMethodCodeFromSummary = model.deliveryMethodCodeFromSummary;
  }

  public static fromV2(
    cartSummary: cartV2.CartSummary,
    modelContext: ModelContext,
  ) {
    return new SummaryModel({
      priceSummary: {
        delivery: PriceModel.fromV2(
          cartSummary.deliverySummary?.price,
          modelContext,
        ),
        tax: PriceModel.fromV2(cartSummary.priceSummary?.tax, modelContext),
        discount: getCartSubtotalAndDeliveryDiscountsTotal(
          cartSummary.discounts,
          modelContext,
        ),
      },
      additionalFees: cartSummary.additionalFees?.map((additionalFee) =>
        AdditionalFeeModel.fromV2(additionalFee, modelContext),
      ),
      giftCardAmount: modelContext.experiments.enabled(
        SPECS.SupportMultipleGCInCheckout,
      )
        ? getGiftCardAmountFromV2(
            cartSummary?.paymentSummary?.giftCards,
            modelContext,
          )
        : PriceModel.fromV2(
            cartSummary?.paymentSummary?.giftCards?.[0]?.redeemAmount,
            modelContext,
          ),
      payNow: PriceModel.fromV2(
        cartSummary.paymentSummary?.payNow,
        modelContext,
      )!,
      lineItemDiscounts: cartSummary.discounts?.flatMap((discount) =>
        (discount.lineItemDiscounts ?? /* istanbul ignore next */ []).map(
          (lineItemDiscount) =>
            LineItemDiscountModel.fromV2(
              discount,
              lineItemDiscount,
              modelContext,
            ),
        ),
      ),
      hasCustomViolations: Boolean(
        cartSummary.violations?.some(
          (validation) =>
            validation.code === cartV2.ViolationCode.CUSTOM_VALIDATION,
        ),
      ),
      calculationErrors: cartSummary.calculationErrors,
      taxExemptPrice: PriceModel.fromV2(
        cartSummary.taxSummary?.totalExempt,
        modelContext,
      ),
      priceVerificationToken: cartSummary.priceVerificationToken ?? undefined,
      deliveryMethodCodeFromSummary: cartSummary.deliverySummary?.method?.code,
    });
  }
}

function getCartSubtotalAndDeliveryDiscountsTotal(
  discounts: cartV2.Discount[] | undefined,
  modelContext: ModelContext,
): PriceModel {
  const total = (discounts ?? [])
    .filter(
      (d) =>
        d.scope === cartV2.DiscountScope.CART_SUBTOTAL ||
        d.scope === cartV2.DiscountScope.DELIVERY,
    )
    .reduce(
      (sum, d) =>
        sum +
        Number(
          PriceModel.fromV2(d.total, modelContext)?.amount ??
            /* istanbul ignore next */ '0',
        ),
      0,
    );

  return new PriceModel({ amount: String(total) });
}

function getGiftCardAmountFromV2(
  giftCards: cartV2.GiftCardSummary[] | undefined,
  modelContext: ModelContext,
): PriceModel | undefined {
  if (!giftCards?.length) {
    return undefined;
  }

  const totalAmount = giftCards.reduce(
    (sum, giftCard) => sum + getGiftCardRedeemValue(giftCard, modelContext),
    0,
  );

  return new PriceModel({ amount: totalAmount.toString() });
}

function getGiftCardRedeemValue(
  giftCard: cartV2.GiftCardSummary,
  { useConvertedPrices }: ModelContext,
): number {
  const redeemAmount = giftCard.redeemAmount;
  if (!redeemAmount) {
    return 0;
  }

  const amount = useConvertedPrices
    ? redeemAmount.convertedAmount
    : redeemAmount.amount;
  if (!amount) {
    return 0;
  }

  return Number(amount);
}
